import SpeakButton from "../EnglishTool/SpeakButton";

export const chapterPages = {
  1: [
    {
      title: <h2>Grade 6 Nouns: Functions & Appositives</h2>,
      body: (
        <>
          <p>
            <b>Nouns</b> can act as a subject, direct object, indirect object, object of a preposition, or predicate noun.<br/>
            <i>Subject:</i> <b>My brother</b> is tall.<br/>
            <i>Object:</i> I called <b>my friend</b>.<br/>
            <SpeakButton text="My brother is tall. I called my friend." />
          </p>
          <p>
            <b>Appositives:</b> A noun or noun phrase that renames another noun.<br/>
            Example: <i>My brother, <b>a soccer player</b>, loves sports.</i>
            <SpeakButton text="My brother, a soccer player, loves sports." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Gerunds & Noun Phrases</h2>,
      body: (
        <>
          <p>
            <b>Gerund:</b> Verb + “-ing” acting as a noun.<br/>
            Example: <i><b>Swimming</b> is fun.</i>
          </p>
          <p>
            <b>Noun phrase:</b> A group of words acting as a noun.<br/>
            Example: <i><b>The sound of rain</b> relaxed her.</i>
            <SpeakButton text="The sound of rain relaxed her." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Abstract Nouns & Nominalization</h2>,
      body: (
        <>
          <p>
            <b>Abstract nouns:</b> name things you cannot see or touch (e.g. <b>freedom, kindness, ambition</b>).
          </p>
          <p>
            <b>Nominalization:</b> Turning a verb or adjective into a noun.<br/>
            <i>decide → decision, happy → happiness</i>
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Noun Skills</h2>,
      body: (
        <>
          <p>
            Find the gerund, noun phrase, and appositive:<br/>
            <b>
              "Traveling the world, my dream, can teach you a lot about life."
              <SpeakButton text="Traveling the world, my dream, can teach you a lot about life." />
            </b>
          </p>
        </>
      ),
    },
  ],

  2: [
    {
      title: <h2>Grade 6 Verbs: Verbals & Voice</h2>,
      body: (
        <>
          <p>
            <b>Participles:</b> Verb forms used as adjectives (<b>burning</b> candle, <b>baked</b> bread).<br/>
            <b>Infinitives:</b> “to” + base verb. <i>to read, to sing</i>
          </p>
          <p>
            <b>Voice:</b> <b>Active:</b> The dog chased the ball. <b>Passive:</b> The ball was chased by the dog.
            <SpeakButton text="The dog chased the ball. The ball was chased by the dog." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Verb Mood</h2>,
      body: (
        <>
          <p>
            <b>Indicative:</b> States facts (<i>He walks</i>.)<br/>
            <b>Imperative:</b> Gives a command (<i>Close the door.</i>)<br/>
            <b>Interrogative:</b> Asks a question (<i>Are you ready?</i>)<br/>
            <b>Conditional:</b> Shows what could happen (<i>If I study, I will pass.</i>)<br/>
            <b>Subjunctive:</b> Expresses a wish or hypothetical (<i>If I were you, I would try.</i>)
            <SpeakButton text="If I were you, I would try." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Perfect and Progressive Tenses Review</h2>,
      body: (
        <>
          <p>
            <b>Perfect tenses:</b> Show completed actions.<br/>
            <b>Progressive tenses:</b> Show ongoing actions.<br/>
            <i>She had been reading when I arrived.</i>
            <SpeakButton text="She had been reading when I arrived." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Verbals, Voice & Mood</h2>,
      body: (
        <>
          <p>
            Change the sentence to passive voice:<br/>
            <b>"The chef cooked dinner."</b>
            <SpeakButton text="The chef cooked dinner." />
          </p>
          <p>
            Write a sentence using the subjunctive mood.
          </p>
        </>
      ),
    },
  ],

  3: [
    {
      title: <h2>Adjectival Participles & Modifier Stacking</h2>,
      body: (
        <>
          <p>
            <b>Participial phrases</b> act as adjectives.<br />
            <i>
              The <b>shivering</b> puppy hid under the blanket.{" "}
              <SpeakButton text="The shivering puppy hid under the blanket." />
            </i>
            <br />
            <i>
              The cake, <b>covered in chocolate</b>, was delicious.{" "}
              <SpeakButton text="The cake, covered in chocolate, was delicious." />
            </i>
          </p>
          <p>
            <b>Modifier stacking</b>: You can use several adjectives before a noun.<br />
            <i>
              A <b>tall, dark, mysterious</b> stranger entered.{" "}
              <SpeakButton text="A tall, dark, mysterious stranger entered." />
            </i>
          </p>
        </>
      ),
    },
    {
      title: <h2>Comparative & Superlative – Multi-syllable & Irregular Forms</h2>,
      body: (
        <>
          <p>
            For adjectives with 2+ syllables, use <b>more</b>/<b>most</b>.<br />
            <i>
              beautiful → more beautiful → most beautiful{" "}
              <SpeakButton text="beautiful, more beautiful, most beautiful" />
            </i>
            <br />
            <i>
              interesting → more interesting → most interesting{" "}
              <SpeakButton text="interesting, more interesting, most interesting" />
            </i>
          </p>
          <p>
            <b>Irregular:</b> little, less, least; far, farther, farthest
            <br />
            <SpeakButton text="little, less, least" />{" "}
            <SpeakButton text="far, farther, farthest" />
          </p>
        </>
      ),
    },
    {
      title: <h2>Attributive vs. Predicative Adjectives</h2>,
      body: (
        <>
          <p>
            <b>Attributive</b>: Before the noun.<br />
            <i>
              The <b>old</b> building collapsed.{" "}
              <SpeakButton text="The old building collapsed." />
            </i>
          </p>
          <p>
            <b>Predicative</b>: After a linking verb.<br />
            <i>
              The building is <b>old</b>.{" "}
              <SpeakButton text="The building is old." />
            </i>
          </p>
        </>
      ),
    },
    {
      title: <h2>Restrictive & Non-restrictive Adjective Clauses</h2>,
      body: (
        <>
          <p>
            <b>Restrictive clause</b>: No commas. Defines the noun.<br />
            <i>
              The dog <b>that barked</b> is mine.{" "}
              <SpeakButton text="The dog that barked is mine." />
            </i>
          </p>
          <p>
            <b>Non-restrictive clause</b>: Uses commas. Adds extra info.<br />
            <i>
              My dog, <b>who is very friendly</b>, loves to play.{" "}
              <SpeakButton text="My dog, who is very friendly, loves to play." />
            </i>
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Advanced Adjective Skills</h2>,
      body: (
        <>
          <p>
            Identify the participial phrase:<br />
            <b>
              The students, <b>excited by the field trip</b>, boarded the bus.{" "}
              <SpeakButton text="The students, excited by the field trip, boarded the bus." />
            </b>
          </p>
          <p>
            Rewrite this using a non-restrictive clause:<br />
            <b>
              The teacher who won an award is speaking today.{" "}
              <SpeakButton text="The teacher who won an award is speaking today." />
            </b>
            <br />
            <i>
              Answer: The teacher, who won an award, is speaking today.{" "}
              <SpeakButton text="The teacher, who won an award, is speaking today." />
            </i>
          </p>
        </>
      ),
    },
  ],
  
  4: [
    {
      title: <h2>Grade 6 Adverbs: Clauses & Phrases</h2>,
      body: (
        <>
          <p>
            <b>Adverb phrase:</b> group of words acting as an adverb<br/>
            <i>She answered <b>with great confidence</b>.</i>
          </p>
          <p>
            <b>Adverb clause:</b> has subject/verb, begins with <b>because, although, if, when, since, after, before, unless</b>.<br/>
            <i>She ran <b>because she was late</b>.</i>
            <SpeakButton text="She ran because she was late." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Intensifiers & Adverb Placement</h2>,
      body: (
        <>
          <p>
            <b>Intensifiers</b> strengthen or weaken adjectives/adverbs: very, quite, too, almost, so, rather, extremely.
            <br/>
            Example: <i>He ran <b>very quickly</b>.</i>
          </p>
          <p>
            Adverbs can go before or after the verb:<br/>
            <i>She often sings. She sings often.</i>
          </p>
        </>
      ),
    },
    {
      title: <h2>Comparative & Superlative Adverbs</h2>,
      body: (
        <>
          <p>
            <b>Comparative:</b> faster, more easily<br/>
            <b>Superlative:</b> fastest, most easily
            <br/>
            Irregular: well, better, best
            <SpeakButton text="well, better, best" />
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Adverb Clauses & Intensifiers</h2>,
      body: (
        <>
          <p>
            Write a sentence using an adverb clause that begins with “since” or “unless.”
          </p>
          <p>
            Find the intensifier:<br/>
            <b>
              "The math problem was extremely difficult."
              <SpeakButton text="The math problem was extremely difficult." />
            </b>
          </p>
        </>
      ),
    },
  ],

  5: [
    {
      title: <h2>Reading: Analyzing Argument & Author’s Purpose</h2>,
      body: (
        <>
          <p>
            <b>Author’s purpose:</b> persuade, inform, entertain, explain.
            <br/>
            <b>Argument:</b> A claim with reasons and evidence.
            <br/>
            <i>Look for thesis statements, supporting facts, and examples.</i>
          </p>
        </>
      ),
    },
    {
      title: <h2>Text Structure & Academic Vocabulary</h2>,
      body: (
        <>
          <p>
            <b>Text structure:</b> cause/effect, compare/contrast, problem/solution, sequence, description.
          </p>
          <p>
            <b>Academic vocabulary:</b> analyze, infer, evidence, context, illustrate, summarize.
            <SpeakButton text="analyze, infer, evidence, context, illustrate, summarize" />
          </p>
        </>
      ),
    },
    {
      title: <h2>Citing Evidence & Critical Reading</h2>,
      body: (
        <>
          <p>
            Always <b>cite evidence</b> from the text to support your answers. Use direct quotes or paraphrase.
          </p>
          <p>
            Example: <i>What evidence shows the main character was brave?</i>
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Reading Analysis</h2>,
      body: (
        <>
          <p>
            Read and answer:<br/>
            <i>
              “During the hike, the sky grew dark and thunder rumbled. Without hesitation, Maya led her friends to safety.”
            </i>
            <SpeakButton text="During the hike, the sky grew dark and thunder rumbled. Without hesitation, Maya led her friends to safety." />
          </p>
          <ul>
            <li>What text structure is used here?</li>
            <li>What is the author’s purpose?</li>
            <li>Cite evidence that shows Maya is a leader.</li>
          </ul>
        </>
      ),
    },
  ],
};